// SentinelRouteTracking.jsx
// Dual map: OpenStreetMap (Leaflet) + Google Maps
// Provider from company user_map

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import { API_BASE_URL } from "../layouts/constants";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/* =========================================================
   GOOGLE MAPS LOADER
========================================================= */

const GOOGLE_MAPS_SCRIPT_ID = "eblackdog-google-maps-script";
const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim() || "";
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };
const DEFAULT_ZOOM = 5;

let googleMapsLoadingPromise = null;

function findExistingGoogleMapsScript() {
  const scriptById = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
  if (scriptById) return scriptById;
  return document.querySelector(
    'script[src*="maps.googleapis.com/maps/api/js"]'
  );
}

function getGoogleMapsErrorMessage(error) {
  const errorText = String(error?.message || error?.name || error || "");
  if (errorText.includes("RefererNotAllowedMapError")) {
    return "This website is not authorized for the Google Maps API key. Add this domain under the API key website restrictions.";
  }
  if (errorText.includes("InvalidKeyMapError")) {
    return "The Google Maps API key is invalid. Check REACT_APP_GOOGLE_MAPS_API_KEY.";
  }
  if (errorText.includes("ApiNotActivatedMapError")) {
    return "Maps JavaScript API is not enabled for this Google Cloud project.";
  }
  if (errorText.includes("BillingNotEnabledMapError")) {
    return "Billing is not enabled for the Google Cloud project used by this API key.";
  }
  if (errorText.includes("MissingKeyMapError")) {
    return "Google Maps API key is missing.";
  }
  return error?.message || "Google Maps JavaScript API could not be loaded.";
}

function loadGoogleMapsScript(apiKey) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(
      new Error("Google Maps can only be loaded in a browser.")
    );
  }
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (!apiKey) {
    return Promise.reject(
      new Error(
        "Google Maps API key is missing. Add REACT_APP_GOOGLE_MAPS_API_KEY to the .env file."
      )
    );
  }
  if (googleMapsLoadingPromise) return googleMapsLoadingPromise;

  googleMapsLoadingPromise = new Promise((resolve, reject) => {
    let settled = false;
    const resolveOnce = () => {
      if (settled || !window.google?.maps) return;
      settled = true;
      resolve(window.google.maps);
    };
    const rejectOnce = (error) => {
      if (settled) return;
      settled = true;
      googleMapsLoadingPromise = null;
      reject(
        error instanceof Error
          ? error
          : new Error("Google Maps JavaScript API failed to load.")
      );
    };
    const handleScriptLoad = () => {
      if (window.google?.maps) {
        resolveOnce();
        return;
      }
      rejectOnce(
        new Error("Google Maps script loaded, but the Maps API is unavailable.")
      );
    };
    const handleScriptError = () => {
      rejectOnce(new Error("Google Maps JavaScript API network request failed."));
    };
    const previousAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      if (typeof previousAuthFailure === "function") {
        try {
          previousAuthFailure();
        } catch (e) {
          console.error(e);
        }
      }
      rejectOnce(
        new Error(
          "Google Maps authentication failed. Check the API key, enabled APIs, billing and website restrictions."
        )
      );
    };
    const existingScript = findExistingGoogleMapsScript();
    if (existingScript) {
      existingScript.addEventListener("load", handleScriptLoad, { once: true });
      existingScript.addEventListener("error", handleScriptError, {
        once: true,
      });
      window.setTimeout(() => {
        if (window.google?.maps) resolveOnce();
      }, 0);
      return;
    }
    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    const queryParameters = new URLSearchParams({
      key: apiKey,
      libraries: "places,geometry",
      v: "weekly",
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${queryParameters.toString()}`;
    script.async = true;
    script.defer = true;
    script.setAttribute("data-google-maps-loader", "eblackdog");
    script.onload = handleScriptLoad;
    script.onerror = () => {
      script.remove();
      handleScriptError();
    };
    document.head.appendChild(script);
  });
  return googleMapsLoadingPromise;
}

function normalizeMapProvider(value) {
  const provider = String(value || "").trim().toLowerCase();
  if (
    provider === "google" ||
    provider === "googlemaps" ||
    provider === "google_maps"
  ) {
    return "google";
  }
  return "openstreetmap";
}

function googleCircleSymbol(color, scale = 10) {
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    scale,
    fillColor: color,
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: 2,
  };
}

/** Nice pin icon for Google Maps (From / To) */
function googlePinIcon(color = "#16a34a", size = 44) {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24">
      <path d="M12 22s7-6.33 7-12a7 7 0 1 0-14 0c0 5.67 7 12 7 12z"
            fill="${color}" fill-opacity="0.18" stroke="${color}" stroke-width="1.5"/>
      <circle cx="12" cy="10" r="3.25" fill="${color}"/>
    </svg>
  `;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(size / 2, size),
  };
}

/* =========================================================
   TRACKING HELPERS
========================================================= */

const ICON = {
  pinSizeFrom: 44,
  pinSizeTo: 44,
  userSize: 48,
  cpRadius: 9,
};

const POLL_MIN_MS = 2000;
const POLL_MAX_MS = 5000;
const POLL_BACKOFF_STEP = 500;
const GAP_MINUTES = 5;
const GAP_MS = GAP_MINUTES * 60 * 1000;
const MAX_JUMP_METERS = 300;
const MAX_ACCURACY_METERS = 20;
const MAX_SPEED_MPS = 15;
const MARKER_ANIMATION_MS = 800;

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

function pinSVG(fill = "#111827", stroke = "#111827", size = 40) {
  return `
  <svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="${stroke}" stroke-width="1.5" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22s7-6.33 7-12a7 7 0 1 0-14 0c0 5.67 7 12 7 12z" fill="${fill}" opacity="0.14"/>
    <circle cx="12" cy="10" r="3.25" fill="${fill}" />
    <path d="M12 22s7-6.33 7-12a7 7 0 1 0-14 0c0 5.67 7 12 7 12z" />
  </svg>`;
}

function userSVG(color = "#2563EB", size = 44) {
  return `
  <svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="${color}" stroke-width="1.5" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="8.5" r="3.5" fill="${color}" opacity="0.12"/>
    <path d="M4 19a8 8 0 0 1 16 0" fill="${color}" opacity="0.08"/>
    <circle cx="12" cy="8.5" r="3.5"/>
    <path d="M4 19a8 8 0 0 1 16 0"/>
  </svg>`;
}

function pinIcon(fill, stroke, size, className = "pin") {
  return L.divIcon({
    className,
    html: pinSVG(fill, stroke, size),
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
  });
}

function userIcon(color, size, className = "userIcon") {
  return L.divIcon({
    className,
    html: `<div class="pulse"></div>${userSVG(color, size)}`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function badgeIcon(text, bg) {
  return L.divIcon({
    className: "",
    html: `
      <div style="
        transform:translate(-50%,-50%);
        background:${bg};
        color:#fff;
        font-size:10px;
        font-weight:600;
        padding:2px 5px;
        border-radius:999px;
        border:1px solid rgba(0,0,0,.25);
        box-shadow:0 1px 3px rgba(0,0,0,.25);
        white-space:nowrap;
      ">${text}</div>`,
    iconSize: [40, 18],
    iconAnchor: [20, 9],
  });
}

function loginIcon() {
  return badgeIcon("LOGIN", "#16a34a");
}

function logoutIcon() {
  return badgeIcon("LOGOUT", "#8b5cf6");
}

function parseDbIst(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts !== "string") return null;
  const s = ts.trim();
  if (!s) return null;
  const clean = s.replace("T", " ").replace("Z", "").split(".")[0];
  const [datePart, timePart] = clean.split(" ");
  if (!datePart || !timePart) return null;
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute, second = 0] = timePart.split(":").map(Number);
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return null;
  }
  const d = new Date(year, month - 1, day, hour, minute, second);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateObj(d) {
  if (!d) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("en-IN", { month: "short" });
  const year = d.getFullYear();
  let hour = d.getHours();
  const minute = String(d.getMinutes()).padStart(2, "0");
  const second = String(d.getSeconds()).padStart(2, "0");
  const ampm = hour >= 12 ? "pm" : "am";
  hour = hour % 12 || 12;
  return `${day} ${month} ${year}, ${String(hour).padStart(
    2,
    "0"
  )}:${minute}:${second} ${ampm}`;
}

function formatISTDateTime(ts) {
  const d = parseDbIst(ts);
  if (!d) return ts || "—";
  return formatDateObj(d);
}

function formatISTFromUtc(ts) {
  if (!ts) return "—";
  if (ts instanceof Date) {
    if (Number.isNaN(ts.getTime())) return "—";
    return new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    })
      .format(ts)
      .replace(",", "");
  }
  if (typeof ts !== "string") return "—";
  const s = ts.trim();
  if (!s) return "—";
  const utcStr = /Z$|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`;
  const d = new Date(utcStr);
  if (Number.isNaN(d.getTime())) return ts || "—";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  })
    .format(d)
    .replace(",", "");
}

function isValidLatLng(point) {
  return (
    point &&
    Number.isFinite(Number(point.lat)) &&
    Number.isFinite(Number(point.lng))
  );
}

function haversineMeters(a, b) {
  const R = 6371000;
  const toRad = (v) => (v * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

function cleanGpsTrack(rawTrack) {
  const ordered = rawTrack
    .filter((p) => isValidLatLng(p))
    .map((p) => ({
      ...p,
      lat: Number(p.lat),
      lng: Number(p.lng),
      accuracy: p.accuracy == null ? null : Number(p.accuracy),
    }))
    .filter((p) => p.accuracy == null || p.accuracy <= MAX_ACCURACY_METERS)
    .sort((a, b) => {
      const da = parseDbIst(a.ts)?.getTime() ?? 0;
      const db = parseDbIst(b.ts)?.getTime() ?? 0;
      return da - db;
    });

  const cleaned = [];
  for (const p of ordered) {
    if (!cleaned.length) {
      cleaned.push(p);
      continue;
    }
    const prev = cleaned[cleaned.length - 1];
    if (prev.lat === p.lat && prev.lng === p.lng && prev.ts === p.ts) continue;
    const dist = haversineMeters(
      { lat: prev.lat, lng: prev.lng },
      { lat: p.lat, lng: p.lng }
    );
    const prevMs = parseDbIst(prev.ts)?.getTime() ?? null;
    const curMs = parseDbIst(p.ts)?.getTime() ?? null;
    const seconds =
      prevMs != null && curMs != null
        ? Math.max(1, (curMs - prevMs) / 1000)
        : 1;
    const speed = dist / seconds;
    if (dist > MAX_JUMP_METERS && speed > MAX_SPEED_MPS) continue;
    cleaned.push(p);
  }
  return cleaned;
}

function insideCircle(lat, lng, centerLat, centerLng, radiusM) {
  return (
    haversineMeters(
      { lat, lng },
      { lat: Number(centerLat), lng: Number(centerLng) }
    ) <= Number(radiusM || 0)
  );
}

function pointInPolygon(lat, lng, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [latI, lngI] = ring[i];
    const [latJ, lngJ] = ring[j];
    const intersect =
      lngI > lng !== lngJ > lng &&
      lat <
        ((latJ - latI) * (lng - lngI)) / ((lngJ - lngI) || 1e-12) + latI;
    if (intersect) inside = !inside;
  }
  return inside;
}

function easeInOutQuad(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

function animateLeafletMarker(marker, from, to, duration = MARKER_ANIMATION_MS) {
  if (!marker || !from || !to) return;
  const start = performance.now();
  function step(now) {
    const rawT = Math.min((now - start) / duration, 1);
    const t = easeInOutQuad(rawT);
    marker.setLatLng([
      from[0] + (to[0] - from[0]) * t,
      from[1] + (to[1] - from[1]) * t,
    ]);
    if (rawT < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

function animateGoogleMarker(marker, from, to, duration = MARKER_ANIMATION_MS) {
  if (!marker || !from || !to || !window.google?.maps) return;
  const start = performance.now();
  function step(now) {
    const rawT = Math.min((now - start) / duration, 1);
    const t = easeInOutQuad(rawT);
    marker.setPosition({
      lat: from[0] + (to[0] - from[0]) * t,
      lng: from[1] + (to[1] - from[1]) * t,
    });
    if (rawT < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

function getLocalDashboardData() {
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  return {
    company_name:
      localStorage.getItem("company_name") || userData.company_name || "",
    licensedFor:
      localStorage.getItem("licensedFor") || userData.licensedFor || "",
    admin_active: Number(
      localStorage.getItem("admin_active") ?? userData.admin_active ?? 0
    ),
    company_address:
      localStorage.getItem("company_address") || userData.company_address || "",
    company_phone:
      localStorage.getItem("company_phone") || userData.company_phone || "",
    admin_email:
      localStorage.getItem("admin_email") || userData.admin_email || "",
    company_logo:
      localStorage.getItem("company_logo") || userData.company_logo || "",
    user_map: normalizeMapProvider(
      localStorage.getItem("user_map") || userData.user_map || ""
    ),
  };
}

/* =========================================================
   COMPONENT
========================================================= */

export default function SentinelRouteTracking() {
  const location = useLocation();

  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState("");

  const dashboardData = useMemo(() => getLocalDashboardData(), []);
  const isGoogleMap = dashboardData.user_map === "google";
  const routePage = useMemo(() => "/sentinel-route-master", []);

  /* Leaflet refs */
  const leafletMapRef = useRef(null);
  const leafletMapElRef = useRef(null);
  const leafletLatestMarkerRef = useRef(null);
  const leafletUserPathRef = useRef(null);
  const leafletCheckpointsRef = useRef(null);
  const leafletGeofenceRef = useRef(null);
  const leafletRouteLineRef = useRef(null);
  const leafletEndPointsRef = useRef(null);
  const leafletLoginLogoutRef = useRef(null);

  /* Google refs */
  const googleMapRef = useRef(null);
  const googleMapElRef = useRef(null);
  const googleLatestMarkerRef = useRef(null);
  const googleUserPathRef = useRef([]);
  const googleCheckpointsRef = useRef([]);
  const googleGeofenceRef = useRef([]);
  const googleRouteLineRef = useRef(null);
  const googleEndPointsRef = useRef([]);
  const googleLoginLogoutRef = useRef([]);

  const pollTimeoutRef = useRef(null);
  const currentPollIntervalRef = useRef(POLL_MIN_MS);
  const lastPositionKeyRef = useRef("");
  const isFetchingRef = useRef(false);
  const didInitialFitRef = useRef(false);
  const staticDrawnRef = useRef(false);
  const lastTrackSignatureRef = useRef("");
  const lastCompletionStatusRef = useRef("");
  const errorShownRef = useRef(false);

  const queryData = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      routeId: Number(
        params.get("route_id") || params.get("sentinel_route_id") || 0
      ),
      userId: params.get("user_id") || params.get("member_id") || "",
      allocId: Number(
        params.get("alloc_id") || params.get("allocation_id") || 0
      ),
      type: params.get("type") || "routes",
    };
  }, [location.search]);

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams({
      route_id: String(queryData.routeId),
      limit: "5000",
      type: queryData.type || "routes",
    });
    if (queryData.userId) params.set("user_id", queryData.userId);
    if (queryData.allocId) params.set("alloc_id", String(queryData.allocId));
    const base = String(API_BASE_URL || "").replace(/\/$/, "");
    const path = base.endsWith("/api")
      ? `${base}/sentinel_route_tracking`
      : `${base}/api/sentinel_route_tracking`;
    return `${path}?${params.toString()}`;
  }, [queryData]);

  const setStatus = useCallback((id, text, type = "neutral") => {
    const el = document.getElementById(id);
    if (!el) return;
    el.textContent = text;
    const base = "px-3 py-1 text-xs font-semibold rounded-full";
    const classes = {
      ok: "bg-emerald-100 text-emerald-700",
      warn: "bg-red-100 text-red-700",
      neutral: "bg-slate-200 text-slate-800",
    };
    el.className = `${base} ${classes[type] || classes.neutral}`;
  }, []);

  const fenceStatusSet = useCallback(
    (text, cls = "neutral") => setStatus("fenceStatus", text, cls),
    [setStatus]
  );

  const routeStatusSet = useCallback(
    (text, cls = "neutral") => setStatus("routeStatus", text, cls),
    [setStatus]
  );

  const applyCompletionStatus = useCallback(
    (completion) => {
      if (!completion?.status) {
        routeStatusSet("Route status: —", "neutral");
        return;
      }
      const status = String(completion.status || "").toUpperCase();
      const label = completion.label || "";
      let cls = "neutral";
      if (["EARLY", "ONTIME", "COMPLETED"].includes(status)) cls = "ok";
      if (["DELAY", "PENDING_DELAY"].includes(status)) cls = "warn";
      routeStatusSet(label || `Route status: ${status}`, cls);
    },
    [routeStatusSet]
  );

  /* ---------- clear ---------- */

  const clearLeaflet = useCallback(() => {
    const map = leafletMapRef.current;
    if (!map) return;
    [
      leafletUserPathRef,
      leafletCheckpointsRef,
      leafletGeofenceRef,
      leafletRouteLineRef,
      leafletEndPointsRef,
      leafletLoginLogoutRef,
      leafletLatestMarkerRef,
    ].forEach((ref) => {
      if (ref.current) {
        map.removeLayer(ref.current);
        ref.current = null;
      }
    });
  }, []);

  const clearGoogle = useCallback(() => {
    googleUserPathRef.current.forEach((l) => l?.setMap(null));
    googleUserPathRef.current = [];
    googleCheckpointsRef.current.forEach((m) => m?.setMap(null));
    googleCheckpointsRef.current = [];
    googleGeofenceRef.current.forEach((m) => m?.setMap(null));
    googleGeofenceRef.current = [];
    googleEndPointsRef.current.forEach((m) => m?.setMap(null));
    googleEndPointsRef.current = [];
    googleLoginLogoutRef.current.forEach((m) => m?.setMap(null));
    googleLoginLogoutRef.current = [];
    if (googleRouteLineRef.current) {
      googleRouteLineRef.current.setMap(null);
      googleRouteLineRef.current = null;
    }
    if (googleLatestMarkerRef.current) {
      googleLatestMarkerRef.current.setMap(null);
      googleLatestMarkerRef.current = null;
    }
  }, []);

  /* ---------- LEAFLET static / live ---------- */

  const drawLeafletStatic = useCallback((data) => {
    const map = leafletMapRef.current;
    if (!map) return;

    const bounds = L.latLngBounds();
    const from = data.route?.meta?.from || null;
    const to = data.route?.meta?.to || null;
    const endMarkers = [];

    if (isValidLatLng(from)) {
      const lat = Number(from.lat);
      const lng = Number(from.lng);
      endMarkers.push(
        L.marker([lat, lng], {
          icon: pinIcon("#16a34a", "#166534", ICON.pinSizeFrom),
        }).bindTooltip("From", { direction: "top" })
      );
      bounds.extend([lat, lng]);
    }
    if (isValidLatLng(to)) {
      const lat = Number(to.lat);
      const lng = Number(to.lng);
      endMarkers.push(
        L.marker([lat, lng], {
          icon: pinIcon("#dc2626", "#991b1b", ICON.pinSizeTo),
        }).bindTooltip("To", { direction: "top" })
      );
      bounds.extend([lat, lng]);
    }
    if (endMarkers.length) {
      leafletEndPointsRef.current = L.layerGroup(endMarkers).addTo(map);
    }

    const routeLine = data.route?.line?.geometry?.coordinates || [];
    if (Array.isArray(routeLine) && routeLine.length >= 2) {
      const coordsLL = routeLine
        .map(([lng, lat]) => [Number(lat), Number(lng)])
        .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
      if (coordsLL.length >= 2) {
        leafletRouteLineRef.current = L.polyline(coordsLL, {
          color: "#0ea5e9",
          weight: 4,
          opacity: 0.7,
          dashArray: "6,8",
          smoothFactor: 2,
        }).addTo(map);
        coordsLL.forEach((c) => bounds.extend(c));
      }
    }

    const cps = Array.isArray(data.checkpoints) ? data.checkpoints : [];
    const cpMarkers = cps
      .filter((cp) => isValidLatLng(cp))
      .map((cp) => {
        const lat = Number(cp.lat);
        const lng = Number(cp.lng);
        bounds.extend([lat, lng]);
        return L.circleMarker([lat, lng], {
          radius: ICON.cpRadius,
          color: "#16a34a",
          weight: 2,
          fillColor: "#22c55e",
          fillOpacity: 0.35,
        }).bindTooltip(cp.checkpoint_name || "Checkpoint", {
          direction: "top",
        });
      });
    if (cpMarkers.length) {
      leafletCheckpointsRef.current = L.layerGroup(cpMarkers).addTo(map);
    }

    const gf = data.geofences?.features || [];
    const gfShapes = [];
    for (const f of gf) {
      const p = f.properties || {};
      if (p.type === "CIRCLE" && f.geometry?.type === "Point") {
        const [lngRaw, latRaw] = f.geometry.coordinates || [];
        const lat = Number(latRaw);
        const lng = Number(lngRaw);
        const radius = Number(p.radius_m || 100);
        if (Number.isFinite(lat) && Number.isFinite(lng) && Number.isFinite(radius)) {
          gfShapes.push(
            L.circle([lat, lng], {
              radius,
              color: "#f59e0b",
              weight: 3,
              fillColor: "#f59e0b",
              fillOpacity: 0.18,
            }).bindPopup(
              `<b>${p.name || "Geofence"}</b><br>Radius: ${radius}m`
            )
          );
          bounds.extend([lat, lng]);
        }
      }
      if (p.type === "POLYGON" && f.geometry?.type === "Polygon") {
        const ring = (f.geometry.coordinates?.[0] || [])
          .map(([lng1, lat1]) => [Number(lat1), Number(lng1)])
          .filter(
            ([lat1, lng1]) => Number.isFinite(lat1) && Number.isFinite(lng1)
          );
        if (ring.length) {
          gfShapes.push(
            L.polygon(ring, {
              color: "#ef4444",
              weight: 2,
              fillColor: "#ef4444",
              fillOpacity: 0.08,
            }).bindPopup(`<b>${p.name || "Fence"}</b>`)
          );
          ring.forEach((c) => bounds.extend(c));
        }
      }
    }
    if (gfShapes.length) {
      leafletGeofenceRef.current = L.layerGroup(gfShapes).addTo(map);
    }

    staticDrawnRef.current = true;
    if (bounds.isValid() && !didInitialFitRef.current) {
      map.fitBounds(bounds.pad(0.15));
      didInitialFitRef.current = true;
    }
  }, []);

  const drawLeafletLive = useCallback(
    (data) => {
      const map = leafletMapRef.current;
      if (!map) return false;

      const bounds = L.latLngBounds();
      const guards = data.gps?.guards || [];
      const completion = data.completion || null;
      const completionStatus = completion
        ? String(completion.status || "").toUpperCase()
        : null;
      const isCompleted =
        completionStatus !== null &&
        completionStatus !== "IN_PROGRESS" &&
        ["EARLY", "ONTIME", "DELAY", "COMPLETED"].includes(completionStatus);
      const completionChanged =
        completionStatus !== lastCompletionStatusRef.current;
      if (completionChanged) {
        lastCompletionStatusRef.current = completionStatus || "";
      }

      let hasTrack = false;
      let latestLLForSession = null;
      let latestTsRawForSession = null;
      let positionChanged = false;

      for (const g of guards) {
        const orderedTrack = cleanGpsTrack(
          Array.isArray(g.track) ? g.track : []
        );
        if (!orderedTrack.length) continue;
        hasTrack = true;

        const segments = [];
        let current = null;
        orderedTrack.forEach((p) => {
          const t = parseDbIst(p.ts);
          const ms = t ? t.getTime() : null;
          const pt = [p.lat, p.lng];
          if (!current) {
            current = {
              points: [pt],
              firstTs: p.ts,
              lastTs: p.ts,
              lastMs: ms,
              lastPt: pt,
            };
            return;
          }
          const timeGap =
            ms !== null &&
            current.lastMs !== null &&
            ms - current.lastMs > GAP_MS;
          if (timeGap) {
            segments.push(current);
            current = {
              points: [pt],
              firstTs: p.ts,
              lastTs: p.ts,
              lastMs: ms,
              lastPt: pt,
            };
          } else {
            current.points.push(pt);
            current.lastTs = p.ts;
            current.lastMs = ms !== null ? ms : current.lastMs;
            current.lastPt = pt;
          }
        });
        if (current?.points.length) segments.push(current);
        if (!segments.length) continue;

        const allPoints = segments.flatMap((s) => s.points);
        const firstTs = segments[0].firstTs;
        const lastSeg = segments[segments.length - 1];
        const lastTs = lastSeg.lastTs;
        if (!allPoints.length) continue;

        const firstPt = allPoints[0];
        const lastPt = allPoints[allPoints.length - 1];
        const trackSignature = `${allPoints.length}-${lastTs}`;
        const posKey = `${lastPt[0].toFixed(6)},${lastPt[1].toFixed(6)},${lastTs}`;
        if (posKey !== lastPositionKeyRef.current) {
          positionChanged = true;
          lastPositionKeyRef.current = posKey;
        }

        const needsTrackRedraw =
          trackSignature !== lastTrackSignatureRef.current ||
          !leafletUserPathRef.current ||
          completionChanged;

        if (needsTrackRedraw) {
          if (leafletUserPathRef.current) {
            map.removeLayer(leafletUserPathRef.current);
            leafletUserPathRef.current = null;
          }
          if (allPoints.length >= 2) {
            const polylines = segments
              .filter((s) => s.points.length >= 2)
              .map((s) =>
                L.polyline(s.points, {
                  weight: 5,
                  opacity: 0.9,
                  color: "#0ea5e9",
                  smoothFactor: 1,
                })
              );
            const dots = segments
              .filter((s) => s.points.length === 1)
              .map((s) =>
                L.circleMarker(s.points[0], {
                  radius: 4,
                  color: "#0ea5e9",
                  fillColor: "#0ea5e9",
                  fillOpacity: 0.8,
                })
              );
            leafletUserPathRef.current = L.layerGroup([
              ...polylines,
              ...dots,
            ]).addTo(map);
            allPoints.forEach((c) => bounds.extend(c));
          } else if (allPoints.length === 1) {
            leafletUserPathRef.current = L.layerGroup([
              L.circleMarker(allPoints[0], {
                radius: 5,
                color: "#0ea5e9",
                fillColor: "#0ea5e9",
                fillOpacity: 0.9,
              }),
            ]).addTo(map);
            bounds.extend(allPoints[0]);
          }
          lastTrackSignatureRef.current = trackSignature;
        } else {
          allPoints.forEach((c) => bounds.extend(c));
        }

        if (completionChanged || !leafletLoginLogoutRef.current) {
          const llMarkers = [
            L.marker(firstPt, { icon: loginIcon() }).bindTooltip(
              `Login<br>${formatISTFromUtc(firstTs)}`,
              { direction: "top" }
            ),
          ];
          bounds.extend(firstPt);
          if (isCompleted) {
            llMarkers.push(
              L.marker(lastPt, { icon: logoutIcon() }).bindTooltip(
                `Logout<br>${formatISTFromUtc(
                  completion?.completed_at || lastTs
                )}`,
                { direction: "top" }
              )
            );
            bounds.extend(lastPt);
          }
          if (leafletLoginLogoutRef.current) {
            map.removeLayer(leafletLoginLogoutRef.current);
          }
          leafletLoginLogoutRef.current = L.layerGroup(llMarkers).addTo(map);
        }

        latestLLForSession = [lastPt[0], lastPt[1]];
        latestTsRawForSession = lastTs;

        const idText = g.guard_id || g.member_id || g.user_id || "";
        const nameText =
          g.guard_name || g.guard_label || g.username || idText || "User";
        const timeIST = formatISTFromUtc(lastTs);
        const lines = [];
        if (nameText) lines.push(`Name: ${nameText}`);
        if (idText && idText !== nameText) lines.push(`ID: ${idText}`);
        if (timeIST) lines.push(`Time (IST): ${timeIST}`);

        if (isCompleted) {
          if (leafletLatestMarkerRef.current) {
            map.removeLayer(leafletLatestMarkerRef.current);
            leafletLatestMarkerRef.current = null;
          }
        } else if (!leafletLatestMarkerRef.current) {
          leafletLatestMarkerRef.current = L.marker(lastPt, {
            icon: userIcon("#2563EB", ICON.userSize),
            zIndexOffset: 1200,
          }).addTo(map);
          leafletLatestMarkerRef.current.bindTooltip(lines.join("<br>"), {
            direction: "top",
          });
          bounds.extend(lastPt);
        } else {
          const old = leafletLatestMarkerRef.current.getLatLng();
          animateLeafletMarker(
            leafletLatestMarkerRef.current,
            [old.lat, old.lng],
            lastPt,
            MARKER_ANIMATION_MS
          );
          leafletLatestMarkerRef.current.bindTooltip(lines.join("<br>"), {
            direction: "top",
          });
          bounds.extend(lastPt);
        }
      }

      const guardInfoEl = document.getElementById("guardInfo");
      if (guardInfoEl) {
        const g = guards[0];
        if (g) {
          const id = g.guard_id || g.member_id || g.user_id || "";
          const name = g.guard_name || g.guard_label || g.username || id || "";
          guardInfoEl.textContent = `Guard: ${
            name && id && name !== id ? `${name} (${id})` : name || id || "—"
          }`;
        } else {
          guardInfoEl.textContent = "Guard: —";
        }
      }

      const lastTsEl = document.getElementById("lastTs");
      if (lastTsEl) {
        lastTsEl.textContent = `Last update (IST): ${formatISTFromUtc(
          latestTsRawForSession
        )}`;
      }

      if (completionStatus) applyCompletionStatus(completion);
      else if (hasTrack) routeStatusSet("Route status: In progress", "neutral");
      else routeStatusSet("Route status: —", "neutral");

      const gf = data.geofences?.features || [];
      let inside = false;
      if (latestLLForSession && Array.isArray(gf) && gf.length) {
        const [lat, lng] = latestLLForSession;
        for (const f of gf) {
          const p = f.properties || {};
          if (p.type === "CIRCLE" && f.geometry?.type === "Point") {
            const [clng, clat] = f.geometry.coordinates || [];
            if (
              Number.isFinite(Number(clat)) &&
              Number.isFinite(Number(clng)) &&
              insideCircle(lat, lng, clat, clng, p.radius_m || 0)
            ) {
              inside = true;
              break;
            }
          }
          if (p.type === "POLYGON" && f.geometry?.type === "Polygon") {
            const ring = (f.geometry.coordinates?.[0] || [])
              .map(([ln, la]) => [Number(la), Number(ln)])
              .filter(
                ([la, ln]) => Number.isFinite(la) && Number.isFinite(ln)
              );
            if (ring.length && pointInPolygon(lat, lng, ring)) {
              inside = true;
              break;
            }
          }
        }
      }

      if (!Array.isArray(gf) || !gf.length) fenceStatusSet("No fence", "neutral");
      else if (inside) fenceStatusSet("Inside geofence", "ok");
      else fenceStatusSet("Outside geofence", "warn");

      if (bounds.isValid() && !didInitialFitRef.current) {
        map.fitBounds(bounds.pad(0.15));
        didInitialFitRef.current = true;
      }

      return positionChanged;
    },
    [applyCompletionStatus, fenceStatusSet, routeStatusSet]
  );

  /* ---------- GOOGLE static / live ---------- */

  const drawGoogleStatic = useCallback((data) => {
    const map = googleMapRef.current;
    if (!map || !window.google?.maps) return;

    const bounds = new window.google.maps.LatLngBounds();
    const from = data.route?.meta?.from || null;
    const to = data.route?.meta?.to || null;

    // ========== FIXED: Proper pin icons for From / To ==========
    if (isValidLatLng(from)) {
      const pos = { lat: Number(from.lat), lng: Number(from.lng) };
      googleEndPointsRef.current.push(
        new window.google.maps.Marker({
          map,
          position: pos,
          title: "From",
          icon: googlePinIcon("#16a34a", 44), // green pin
          zIndex: 900,
        })
      );
      bounds.extend(pos);
    }

    if (isValidLatLng(to)) {
      const pos = { lat: Number(to.lat), lng: Number(to.lng) };
      googleEndPointsRef.current.push(
        new window.google.maps.Marker({
          map,
          position: pos,
          title: "To",
          icon: googlePinIcon("#dc2626", 44), // red pin
          zIndex: 900,
        })
      );
      bounds.extend(pos);
    }
    // ===========================================================

    const routeLine = data.route?.line?.geometry?.coordinates || [];
    if (Array.isArray(routeLine) && routeLine.length >= 2) {
      const path = routeLine
        .map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) }))
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
      if (path.length >= 2) {
        googleRouteLineRef.current = new window.google.maps.Polyline({
          map,
          path,
          strokeColor: "#0ea5e9",
          strokeOpacity: 0.7,
          strokeWeight: 4,
        });
        path.forEach((p) => bounds.extend(p));
      }
    }

    const cps = Array.isArray(data.checkpoints) ? data.checkpoints : [];
    cps.filter(isValidLatLng).forEach((cp) => {
      const pos = { lat: Number(cp.lat), lng: Number(cp.lng) };
      googleCheckpointsRef.current.push(
        new window.google.maps.Marker({
          map,
          position: pos,
          title: cp.checkpoint_name || "Checkpoint",
          icon: googleCircleSymbol("#22c55e", 8),
        })
      );
      bounds.extend(pos);
    });

    const gf = data.geofences?.features || [];
    gf.forEach((f) => {
      const p = f.properties || {};
      if (p.type === "CIRCLE" && f.geometry?.type === "Point") {
        const [lngRaw, latRaw] = f.geometry.coordinates || [];
        const lat = Number(latRaw);
        const lng = Number(lngRaw);
        const radius = Number(p.radius_m || 100);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          googleGeofenceRef.current.push(
            new window.google.maps.Circle({
              map,
              center: { lat, lng },
              radius,
              strokeColor: "#f59e0b",
              strokeWeight: 3,
              fillColor: "#f59e0b",
              fillOpacity: 0.18,
            })
          );
          bounds.extend({ lat, lng });
        }
      }
      if (p.type === "POLYGON" && f.geometry?.type === "Polygon") {
        const path = (f.geometry.coordinates?.[0] || [])
          .map(([ln, la]) => ({ lat: Number(la), lng: Number(ln) }))
          .filter((x) => Number.isFinite(x.lat) && Number.isFinite(x.lng));
        if (path.length) {
          googleGeofenceRef.current.push(
            new window.google.maps.Polygon({
              map,
              paths: path,
              strokeColor: "#ef4444",
              strokeWeight: 2,
              fillColor: "#ef4444",
              fillOpacity: 0.08,
            })
          );
          path.forEach((pt) => bounds.extend(pt));
        }
      }
    });

    staticDrawnRef.current = true;
    if (!bounds.isEmpty() && !didInitialFitRef.current) {
      map.fitBounds(bounds, 48);
      didInitialFitRef.current = true;
    }
  }, []);

  const drawGoogleLive = useCallback(
    (data) => {
      const map = googleMapRef.current;
      if (!map || !window.google?.maps) return false;

      const bounds = new window.google.maps.LatLngBounds();
      const guards = data.gps?.guards || [];
      const completion = data.completion || null;
      const completionStatus = completion
        ? String(completion.status || "").toUpperCase()
        : null;
      const isCompleted =
        completionStatus !== null &&
        completionStatus !== "IN_PROGRESS" &&
        ["EARLY", "ONTIME", "DELAY", "COMPLETED"].includes(completionStatus);
      const completionChanged =
        completionStatus !== lastCompletionStatusRef.current;
      if (completionChanged) {
        lastCompletionStatusRef.current = completionStatus || "";
      }

      let hasTrack = false;
      let latestLLForSession = null;
      let latestTsRawForSession = null;
      let positionChanged = false;

      for (const g of guards) {
        const orderedTrack = cleanGpsTrack(
          Array.isArray(g.track) ? g.track : []
        );
        if (!orderedTrack.length) continue;
        hasTrack = true;

        const allPoints = orderedTrack.map((p) => ({
          lat: p.lat,
          lng: p.lng,
        }));
        const firstTs = orderedTrack[0].ts;
        const lastTs = orderedTrack[orderedTrack.length - 1].ts;
        const firstPt = [orderedTrack[0].lat, orderedTrack[0].lng];
        const lastPt = [
          orderedTrack[orderedTrack.length - 1].lat,
          orderedTrack[orderedTrack.length - 1].lng,
        ];

        const trackSignature = `${allPoints.length}-${lastTs}`;
        const posKey = `${lastPt[0].toFixed(6)},${lastPt[1].toFixed(6)},${lastTs}`;
        if (posKey !== lastPositionKeyRef.current) {
          positionChanged = true;
          lastPositionKeyRef.current = posKey;
        }

        const needsTrackRedraw =
          trackSignature !== lastTrackSignatureRef.current ||
          !googleUserPathRef.current.length ||
          completionChanged;

        if (needsTrackRedraw) {
          googleUserPathRef.current.forEach((l) => l?.setMap(null));
          googleUserPathRef.current = [];
          if (allPoints.length >= 2) {
            const poly = new window.google.maps.Polyline({
              map,
              path: allPoints,
              strokeColor: "#0ea5e9",
              strokeOpacity: 0.9,
              strokeWeight: 5,
            });
            googleUserPathRef.current.push(poly);
            allPoints.forEach((p) => bounds.extend(p));
          }
          lastTrackSignatureRef.current = trackSignature;
        } else {
          allPoints.forEach((p) => bounds.extend(p));
        }

        if (completionChanged || !googleLoginLogoutRef.current.length) {
          googleLoginLogoutRef.current.forEach((m) => m?.setMap(null));
          googleLoginLogoutRef.current = [];
          googleLoginLogoutRef.current.push(
            new window.google.maps.Marker({
              map,
              position: { lat: firstPt[0], lng: firstPt[1] },
              title: `Login ${formatISTFromUtc(firstTs)}`,
              label: {
                text: "IN",
                color: "#fff",
                fontSize: "10px",
                fontWeight: "700",
              },
              icon: googleCircleSymbol("#16a34a", 11),
            })
          );
          bounds.extend({ lat: firstPt[0], lng: firstPt[1] });
          if (isCompleted) {
            googleLoginLogoutRef.current.push(
              new window.google.maps.Marker({
                map,
                position: { lat: lastPt[0], lng: lastPt[1] },
                title: `Logout ${formatISTFromUtc(
                  completion?.completed_at || lastTs
                )}`,
                label: {
                  text: "OUT",
                  color: "#fff",
                  fontSize: "10px",
                  fontWeight: "700",
                },
                icon: googleCircleSymbol("#8b5cf6", 11),
              })
            );
            bounds.extend({ lat: lastPt[0], lng: lastPt[1] });
          }
        }

        latestLLForSession = lastPt;
        latestTsRawForSession = lastTs;

        const idText = g.guard_id || g.member_id || g.user_id || "";
        const nameText =
          g.guard_name || g.guard_label || g.username || idText || "User";

        if (isCompleted) {
          if (googleLatestMarkerRef.current) {
            googleLatestMarkerRef.current.setMap(null);
            googleLatestMarkerRef.current = null;
          }
        } else if (!googleLatestMarkerRef.current) {
          googleLatestMarkerRef.current = new window.google.maps.Marker({
            map,
            position: { lat: lastPt[0], lng: lastPt[1] },
            title: `${nameText} · ${formatISTFromUtc(lastTs)}`,
            icon: googleCircleSymbol("#2563EB", 12),
            zIndex: 1200,
          });
          bounds.extend({ lat: lastPt[0], lng: lastPt[1] });
        } else {
          const old = googleLatestMarkerRef.current.getPosition();
          if (old) {
            animateGoogleMarker(
              googleLatestMarkerRef.current,
              [old.lat(), old.lng()],
              lastPt,
              MARKER_ANIMATION_MS
            );
          } else {
            googleLatestMarkerRef.current.setPosition({
              lat: lastPt[0],
              lng: lastPt[1],
            });
          }
          googleLatestMarkerRef.current.setTitle(
            `${nameText} · ${formatISTFromUtc(lastTs)}`
          );
          bounds.extend({ lat: lastPt[0], lng: lastPt[1] });
        }
      }

      const guardInfoEl = document.getElementById("guardInfo");
      if (guardInfoEl) {
        const g = guards[0];
        if (g) {
          const id = g.guard_id || g.member_id || g.user_id || "";
          const name = g.guard_name || g.guard_label || g.username || id || "";
          guardInfoEl.textContent = `Guard: ${
            name && id && name !== id ? `${name} (${id})` : name || id || "—"
          }`;
        } else {
          guardInfoEl.textContent = "Guard: —";
        }
      }

      const lastTsEl = document.getElementById("lastTs");
      if (lastTsEl) {
        lastTsEl.textContent = `Last update (IST): ${formatISTFromUtc(
          latestTsRawForSession
        )}`;
      }

      if (completionStatus) applyCompletionStatus(completion);
      else if (hasTrack) routeStatusSet("Route status: In progress", "neutral");
      else routeStatusSet("Route status: —", "neutral");

      const gf = data.geofences?.features || [];
      let inside = false;
      if (latestLLForSession && Array.isArray(gf) && gf.length) {
        const [lat, lng] = latestLLForSession;
        for (const f of gf) {
          const p = f.properties || {};
          if (p.type === "CIRCLE" && f.geometry?.type === "Point") {
            const [clng, clat] = f.geometry.coordinates || [];
            if (
              Number.isFinite(Number(clat)) &&
              Number.isFinite(Number(clng)) &&
              insideCircle(lat, lng, clat, clng, p.radius_m || 0)
            ) {
              inside = true;
              break;
            }
          }
          if (p.type === "POLYGON" && f.geometry?.type === "Polygon") {
            const ring = (f.geometry.coordinates?.[0] || [])
              .map(([ln, la]) => [Number(la), Number(ln)])
              .filter(
                ([la, ln]) => Number.isFinite(la) && Number.isFinite(ln)
              );
            if (ring.length && pointInPolygon(lat, lng, ring)) {
              inside = true;
              break;
            }
          }
        }
      }

      if (!Array.isArray(gf) || !gf.length) fenceStatusSet("No fence", "neutral");
      else if (inside) fenceStatusSet("Inside geofence", "ok");
      else fenceStatusSet("Outside geofence", "warn");

      if (!bounds.isEmpty() && !didInitialFitRef.current) {
        map.fitBounds(bounds, 48);
        didInitialFitRef.current = true;
      }

      return positionChanged;
    },
    [applyCompletionStatus, fenceStatusSet, routeStatusSet]
  );

  /* ---------- poll ---------- */

  const updatePollInterval = useCallback((positionChanged, isCompleted) => {
    if (isCompleted) currentPollIntervalRef.current = POLL_MAX_MS;
    else if (positionChanged) currentPollIntervalRef.current = POLL_MIN_MS;
    else {
      currentPollIntervalRef.current = Math.min(
        currentPollIntervalRef.current + POLL_BACKOFF_STEP,
        POLL_MAX_MS
      );
    }
    const el = document.getElementById("pollIndicator");
    if (el) {
      el.textContent = `↻ ${(currentPollIntervalRef.current / 1000).toFixed(
        1
      )}s`;
    }
  }, []);

  const drawAll = useCallback(
    (data) => {
      if (isGoogleMap) {
        if (!googleMapRef.current) {
          return { positionChanged: false, isCompleted: false };
        }
        if (!staticDrawnRef.current) drawGoogleStatic(data);
        const positionChanged = drawGoogleLive(data);
        const completionStatus = data.completion
          ? String(data.completion.status || "").toUpperCase()
          : null;
        const isCompleted = ["EARLY", "ONTIME", "DELAY", "COMPLETED"].includes(
          completionStatus
        );
        const sessionRangeEl = document.getElementById("sessionRange");
        if (sessionRangeEl) {
          const alloc = data.allocation || null;
          const range = data.range || null;
          const startRaw = alloc?.start_time || range?.start || null;
          const endRaw = alloc?.end_time || range?.end || null;
          sessionRangeEl.textContent = `Session: ${
            startRaw ? formatISTDateTime(startRaw) : "—"
          } → ${endRaw ? formatISTDateTime(endRaw) : "—"}`;
        }
        return { positionChanged: positionChanged ?? false, isCompleted };
      }

      if (!leafletMapRef.current) {
        return { positionChanged: false, isCompleted: false };
      }
      if (!staticDrawnRef.current) drawLeafletStatic(data);
      const positionChanged = drawLeafletLive(data);
      const completionStatus = data.completion
        ? String(data.completion.status || "").toUpperCase()
        : null;
      const isCompleted = ["EARLY", "ONTIME", "DELAY", "COMPLETED"].includes(
        completionStatus
      );
      const sessionRangeEl = document.getElementById("sessionRange");
      if (sessionRangeEl) {
        const alloc = data.allocation || null;
        const range = data.range || null;
        const startRaw = alloc?.start_time || range?.start || null;
        const endRaw = alloc?.end_time || range?.end || null;
        sessionRangeEl.textContent = `Session: ${
          startRaw ? formatISTDateTime(startRaw) : "—"
        } → ${endRaw ? formatISTDateTime(endRaw) : "—"}`;
      }
      return { positionChanged: positionChanged ?? false, isCompleted };
    },
    [
      isGoogleMap,
      drawLeafletStatic,
      drawLeafletLive,
      drawGoogleStatic,
      drawGoogleLive,
    ]
  );

  const refresh = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    let positionChanged = false;
    let isCompleted = false;
    try {
      const token = getToken();
      if (!token) throw new Error("Login token missing. Please login again.");
      const response = await fetch(apiUrl, {
        cache: "no-store",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        const raw = await response.text();
        console.error("Non-JSON response:", raw.slice(0, 500));
        throw new Error(`API returned non-JSON (${response.status})`);
      }
      const data = await response.json();
      if (!response.ok || (!data.ok && data.success === false)) {
        throw new Error(
          data?.error || data?.message || `HTTP ${response.status}`
        );
      }
      errorShownRef.current = false;
      const result = drawAll(data);
      positionChanged = result.positionChanged;
      isCompleted = result.isCompleted;
    } catch (error) {
      fenceStatusSet(error.message || "API error", "warn");
      console.error("[sentinel_route_tracking] fetch failed:", error);
      if (!errorShownRef.current) {
        toast.error(error.message || "Failed to fetch tracking data");
        errorShownRef.current = true;
      }
    } finally {
      isFetchingRef.current = false;
      updatePollInterval(positionChanged, isCompleted);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = setTimeout(
        () => refresh(),
        currentPollIntervalRef.current
      );
    }
  }, [apiUrl, drawAll, fenceStatusSet, updatePollInterval]);

  /* ---------- init maps ---------- */

  useEffect(() => {
    if (!queryData.routeId) {
      toast.error("Route ID is required for tracking");
      return undefined;
    }

    let cancelled = false;
    setMapError("");
    didInitialFitRef.current = false;
    staticDrawnRef.current = false;
    lastTrackSignatureRef.current = "";
    lastCompletionStatusRef.current = "";
    lastPositionKeyRef.current = "";
    currentPollIntervalRef.current = POLL_MIN_MS;
    errorShownRef.current = false;

    if (isGoogleMap) {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
      setMapLoading(true);
      (async () => {
        try {
          await loadGoogleMapsScript(GOOGLE_MAPS_API_KEY);
          if (cancelled || !googleMapElRef.current) return;
          const map = new window.google.maps.Map(googleMapElRef.current, {
            center: DEFAULT_CENTER,
            zoom: DEFAULT_ZOOM,
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true,
            zoomControl: true,
            clickableIcons: false,
          });
          googleMapRef.current = map;
          setMapLoading(false);
          refresh();
        } catch (error) {
          console.error("Google Maps init error:", error);
          const message = getGoogleMapsErrorMessage(error);
          setMapError(message);
          setMapLoading(false);
          toast.error(message);
        }
      })();
      return () => {
        cancelled = true;
        if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
        clearGoogle();
        googleMapRef.current = null;
      };
    }

    clearGoogle();
    googleMapRef.current = null;
    if (!leafletMapElRef.current) return undefined;

    const map = L.map(leafletMapElRef.current, {
      zoomControl: true,
    }).setView([DEFAULT_CENTER.lat, DEFAULT_CENTER.lng], DEFAULT_ZOOM);
    leafletMapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 20,
      attribution: "&copy; OpenStreetMap",
    }).addTo(map);
    L.control.scale({ imperial: false }).addTo(map);

    const legend = L.control({ position: "bottomright" });
    legend.onAdd = () => {
      const d = L.DomUtil.create("div", "legend");
      d.innerHTML = `
        <div><span class="chip" style="background:#0ea5e9"></span>Track</div>
        <div><span class="chip" style="background:#ef4444"></span>Fence Polygon</div>
        <div><span class="chip" style="background:#f59e0b"></span>Fence Circle</div>
        <div><span class="chip" style="background:#22c55e"></span>Checkpoints</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#16a34a;border-radius:3px;"></span> From</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#dc2626;border-radius:3px;"></span> To</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#2563EB;border-radius:50%;"></span> Live user</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#16a34a;border-radius:999px;"></span> Login</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#8b5cf6;border-radius:999px;"></span> Logout</div>
      `;
      return d;
    };
    legend.addTo(map);

    refresh();

    return () => {
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      clearLeaflet();
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryData.routeId, isGoogleMap]);

  if (!queryData.routeId) {
    return (
      <Layout
        dashboardData={dashboardData}
        routePage={routePage}
        activePage="sentinel-tracking"
        activeMenu="sentinel-tracking"
      >
        <Toaster position="top-right" />
        <div className="min-h-[calc(100vh-90px)] bg-[var(--page-bg)] p-6">
          <div className="rounded-2xl border border-[var(--red-border)] bg-[var(--red-faint)] p-5 text-sm font-medium text-[var(--red)] shadow-sm">
            route_id is required. Example:{" "}
            <span className="font-bold">?route_id=123</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="sentinel-tracking"
      activeMenu="sentinel-tracking"
    >
      <Toaster position="top-right" />

      <style>{`
        .sentinel-map-el { height: 100%; width: 100%; min-height: 520px; }
        .legend {
          background: var(--card-bg);
          padding: 10px 12px;
          border-radius: 10px;
          box-shadow: 0 2px 12px rgba(0,0,0,.12);
          font: 12px/1.25 system-ui;
          color: var(--text-primary);
          border: 1px solid var(--card-border);
        }
        .legend div {
          margin: 6px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .chip {
          display: inline-block;
          width: 14px;
          height: 14px;
          border-radius: 3px;
        }
        .pin { transform: translate(-50%, -100%); }
        .userIcon { transform: translate(-50%, -50%); }
        .pulse {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 18px;
          height: 18px;
          border-radius: 999px;
          transform: translate(-50%, -50%);
          animation: pulse 1.6s infinite;
        }
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0px rgba(37,99,235,.55); }
          70% { box-shadow: 0 0 0 18px rgba(37,99,235,0); }
          100% { box-shadow: 0 0 0 0px rgba(37,99,235,0); }
        }
      `}</style>

      <div className="min-h-[calc(100vh-90px)] bg-[var(--page-bg)] p-4 md:p-6">
        <div className="tracking-container flex h-[calc(100vh-120px)] min-h-[650px] flex-col overflow-hidden rounded-3xl border border-[var(--card-border)] bg-[var(--card-bg)] shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <header className="tracking-header border-b border-[var(--divider)] bg-[var(--card-bg)] px-4 py-4 md:px-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-[var(--text-faint)]">
                  Live Tracking
                </p>
                <h1 className="mt-1 text-xl font-extrabold text-[var(--text-primary)] md:text-2xl">
                  {queryData.type === "place" ? "Place" : "Sentinel Route"} #
                  {queryData.routeId}
                  {queryData.allocId ? (
                    <span className="text-[var(--text-muted)]">
                      {" "}
                      — Allocation #{queryData.allocId}
                    </span>
                  ) : null}
                </h1>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="rounded-full bg-[var(--surface)] px-3 py-1.5 text-xs font-extrabold text-[var(--text-primary)] border border-[var(--surface-border)]">
                  {isGoogleMap ? "Google Maps" : "OpenStreetMap"}
                </div>

                <div
                  id="guardInfo"
                  className="rounded-full bg-[var(--surface)] px-3 py-1.5 text-sm font-medium text-[var(--text-secondary)]"
                >
                  Guard: —
                </div>
                <div
                  id="sessionRange"
                  className="rounded-full bg-[var(--surface)] px-3 py-1.5 text-xs font-medium text-[var(--text-muted)]"
                >
                  Session: —
                </div>
                <div
                  id="routeStatus"
                  className="rounded-full bg-[var(--surface)] px-3 py-1 text-xs font-extrabold text-[var(--text-primary)]"
                >
                  Route status: —
                </div>
                <div
                  id="fenceStatus"
                  className="rounded-full bg-[var(--surface)] px-3 py-1 text-xs font-extrabold text-[var(--text-primary)]"
                >
                  No fence
                </div>
                <div
                  id="lastTs"
                  className="rounded-full bg-[var(--surface)] px-3 py-1.5 text-xs font-medium text-[var(--text-primary)]"
                >
                  —
                </div>
                <div
                  id="pollIndicator"
                  title="Live refresh interval"
                  className="rounded-full bg-[var(--accent)] px-3 py-1 text-xs font-extrabold text-white"
                >
                  ↻ 2.0s
                </div>
              </div>
            </div>
          </header>

          <div className="relative flex-1 bg-[var(--surface)]">
            {!isGoogleMap && (
              <div
                ref={leafletMapElRef}
                className="sentinel-map-el absolute inset-0"
              />
            )}
            {isGoogleMap && (
              <div
                ref={googleMapElRef}
                className="sentinel-map-el absolute inset-0"
              />
            )}

            {mapLoading && (
              <div className="absolute inset-0 z-[1000] flex flex-col items-center justify-center gap-3 bg-[var(--card-bg)]/90 backdrop-blur-sm">
                <div className="h-9 w-9 animate-spin rounded-full border-4 border-[var(--accent-faint)] border-t-[var(--accent)]" />
                <p className="text-sm font-extrabold text-[var(--text-primary)]">
                  Loading Google Maps...
                </p>
              </div>
            )}

            {mapError && (
              <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-[var(--surface)] p-5">
                <div className="w-full max-w-md rounded-2xl border border-red-300 bg-white p-5 text-center shadow-xl">
                  <p className="text-sm font-extrabold text-red-700">
                    Map could not be loaded
                  </p>
                  <p className="mt-2 text-xs font-semibold text-red-600">
                    {mapError}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}